import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Download, ShoppingBag, Package, Truck, CheckCircle, Clock, Printer, X } from 'lucide-react';
import { toast } from 'sonner';

export default function DownloadPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [purchases, setPurchases] = useState([]);
  const [fulfillments, setFulfillments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Shipping Modal State
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [selectedFulfillment, setSelectedFulfillment] = useState(null);
  const [shippingForm, setShippingForm] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
    size: 'M'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { 
    loadData(); 
    // Check if we should open a fulfillment modal
    const fulfillmentId = searchParams.get('fulfillment');
    if (fulfillmentId) {
      setActiveTab('pod');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [purchasesRes, fulfillmentsRes] = await Promise.all([
        api.get('/downloads/my'),
        api.get('/fulfillments')
      ]);
      setPurchases(purchasesRes.data || []);
      setFulfillments(fulfillmentsRes.data?.fulfillments || []);
    } catch (err) { 
      console.error('Load error:', err);
      toast.error('Failed to load purchases'); 
    }
    finally { setLoading(false); }
  };

  const handleDownload = (purchaseId, fileIndex, fileName) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('sd_token');
    const url = `${backendUrl}/api/downloads/file/${purchaseId}/${fileIndex}`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        toast.error('Download failed - access denied');
      }
    };
    xhr.onerror = () => toast.error('Download failed');
    xhr.send();
  };

  const openShippingModal = (fulfillment) => {
    setSelectedFulfillment(fulfillment);
    setShippingForm({
      name: user?.display_name || user?.username || '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      phone: '',
      size: fulfillment.variant_details?.size || 'M'
    });
    setShowShippingModal(true);
  };

  const handleSubmitShipping = async (e) => {
    e.preventDefault();
    if (!selectedFulfillment) return;
    
    // Validate required fields
    if (!shippingForm.name || !shippingForm.address_line1 || !shippingForm.city || 
        !shippingForm.state || !shippingForm.postal_code || !shippingForm.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post(`/fulfillments/${selectedFulfillment.id}/complete`, shippingForm);
      toast.success(res.data.message || 'Order sent to production!');
      setShowShippingModal(false);
      setSelectedFulfillment(null);
      loadData(); // Refresh data
    } catch (err) {
      console.error('Fulfillment error:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete order');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending_shipping_info':
        return <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">Needs Shipping Info</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30">In Production</Badge>;
      case 'shipped':
        return <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">Shipped</Badge>;
      case 'delivered':
        return <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Delivered</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getMimeIcon = (mime) => {
    if (mime?.startsWith('image')) return 'IMG';
    if (mime?.includes('pdf')) return 'PDF';
    if (mime?.includes('zip') || mime?.includes('archive')) return 'ZIP';
    if (mime?.includes('video')) return 'VID';
    if (mime?.includes('audio')) return 'AUD';
    return 'FILE';
  };

  // Filter purchases based on tab
  const digitalPurchases = purchases.filter(p => !p.is_pod_product);
  const podPurchases = purchases.filter(p => p.is_pod_product);

  const displayedPurchases = activeTab === 'all' ? purchases : 
    activeTab === 'digital' ? digitalPurchases : [];

  return (
    <div className="max-w-3xl mx-auto animate-in" data-testid="download-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-2">My Purchases</h1>
      <p className="text-sm text-muted-foreground mb-6">Your purchased products and print orders</p>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-border/40 pb-4">
        <Button 
          variant={activeTab === 'all' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setActiveTab('all')}
          className="rounded-sm text-xs"
          data-testid="tab-all"
        >
          All ({purchases.length})
        </Button>
        <Button 
          variant={activeTab === 'digital' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setActiveTab('digital')}
          className="rounded-sm text-xs"
          data-testid="tab-digital"
        >
          <Download size={14} className="mr-1" /> Digital ({digitalPurchases.length})
        </Button>
        <Button 
          variant={activeTab === 'pod' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setActiveTab('pod')}
          className="rounded-sm text-xs"
          data-testid="tab-pod"
        >
          <Printer size={14} className="mr-1" /> Print Orders ({fulfillments.length})
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading...</div>
      ) : activeTab === 'pod' ? (
        /* Print-on-Demand Fulfillments Tab */
        fulfillments.length === 0 ? (
          <div className="text-center py-16 border border-border/40" data-testid="pod-empty-state">
            <Printer size={36} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-sm font-medium text-foreground mb-1">No print orders yet</p>
            <p className="text-xs text-muted-foreground mb-6">Custom products you purchase will appear here.</p>
            <Link to="/marketplace">
              <Button variant="outline" className="rounded-sm text-xs uppercase tracking-wider">
                Browse Designs
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {fulfillments.map((f, i) => (
              <div
                key={f.id}
                className="border border-border/40 hover:border-gold/30 transition-colors duration-500 animate-in overflow-hidden"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`fulfillment-card-${f.id}`}
              >
                <div className="flex items-start gap-4 p-5">
                  <div className="w-20 h-20 bg-mist shrink-0 flex items-center justify-center border border-border/20 overflow-hidden">
                    <Package size={28} className="text-muted-foreground/40" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-base mb-1">{f.product_title || 'Custom T-Shirt'}</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {getStatusBadge(f.status)}
                      {f.gelato_order_id && (
                        <span className="text-xs text-muted-foreground">#{f.gelato_order_id}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>Size: {f.variant_details?.size || 'M'} • Color: {f.variant_details?.color || 'White'}</p>
                      <p>Ordered: {new Date(f.created_at).toLocaleDateString()}</p>
                      {f.estimated_delivery && f.status !== 'pending_shipping_info' && (
                        <p className="text-emerald-600">Est. Delivery: {f.estimated_delivery}</p>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    {f.status === 'pending_shipping_info' ? (
                      <Button 
                        onClick={() => openShippingModal(f)}
                        className="rounded-sm text-xs"
                        data-testid={`complete-order-btn-${f.id}`}
                      >
                        <Truck size={14} className="mr-1" /> Add Shipping
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle size={16} />
                        <span className="text-xs">In Progress</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Timeline */}
                {f.status !== 'pending_shipping_info' && (
                  <div className="px-5 pb-4 pt-2 border-t border-border/20 bg-mist/30">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${f.status ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span>Ordered</span>
                      </div>
                      <div className="flex-1 h-px bg-border/40 mx-2" />
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${['processing', 'shipped', 'delivered'].includes(f.status) ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span>Production</span>
                      </div>
                      <div className="flex-1 h-px bg-border/40 mx-2" />
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${['shipped', 'delivered'].includes(f.status) ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span>Shipped</span>
                      </div>
                      <div className="flex-1 h-px bg-border/40 mx-2" />
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${f.status === 'delivered' ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span>Delivered</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : displayedPurchases.length === 0 ? (
        <div className="text-center py-16 border border-border/40" data-testid="downloads-empty-state">
          <ShoppingBag size={36} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-sm font-medium text-foreground mb-1">No purchases yet</p>
          <p className="text-xs text-muted-foreground mb-6">Products you buy will appear here.</p>
          <Link to="/marketplace">
            <Button data-testid="browse-marketplace-btn" variant="outline" className="rounded-sm text-xs uppercase tracking-wider">
              Browse Marketplace
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {displayedPurchases.map((purchase, i) => (
            <div
              key={purchase.id}
              className="border border-border/40 hover:border-gold/30 transition-colors duration-500 animate-in overflow-hidden"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`purchase-card-${purchase.id}`}
            >
              {/* Product Header */}
              <div className="flex items-start gap-4 p-5 border-b border-border/20">
                <div className="w-16 h-16 bg-mist shrink-0 flex items-center justify-center border border-border/20 overflow-hidden">
                  {purchase.preview_image ? (
                    <img src={purchase.preview_image.startsWith('data:') ? purchase.preview_image : `data:image/jpeg;base64,${purchase.preview_image}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Package size={22} className="text-muted-foreground/30" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-base mb-1">{purchase.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>Purchased {new Date(purchase.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    {purchase.is_pod_product ? (
                      <Badge className="bg-purple-500/20 text-purple-600 border-purple-500/30 text-[10px]">Print Order</Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{purchase.download_files?.length || 0} files</Badge>
                    )}
                  </div>
                </div>
                <span className="text-lg font-serif text-gold shrink-0">€{purchase.price?.toFixed(2)}</span>
              </div>

              {/* Files List (for digital products) */}
              {!purchase.is_pod_product && (
                <div className="p-4 space-y-2 bg-mist/30">
                  {purchase.download_files?.length > 0 ? (
                    purchase.download_files.map((file, fi) => (
                      <div
                        key={fi}
                        className="flex items-center gap-3 bg-white border border-border/30 p-3 hover:border-gold/30 transition-colors group"
                        data-testid={`download-file-${purchase.id}-${fi}`}
                      >
                        <div className="w-8 h-8 bg-obsidian text-white flex items-center justify-center text-[9px] font-bold uppercase shrink-0">
                          {getMimeIcon(file.mime_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-[10px] text-muted-foreground">{file.mime_type}</p>
                        </div>
                        <Button
                          data-testid={`download-btn-${purchase.id}-${fi}`}
                          variant="outline"
                          size="sm"
                          className="rounded-sm text-xs uppercase tracking-wider shrink-0 border-gold/50 text-gold hover:bg-gold hover:text-obsidian transition-colors"
                          onClick={() => handleDownload(purchase.id, fi, file.name)}
                        >
                          <Download size={12} className="mr-1" /> Download
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2">No downloadable files for this product.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shipping Modal */}
      {showShippingModal && selectedFulfillment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto" data-testid="shipping-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-serif text-lg">Shipping Information</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowShippingModal(false)}>
                <X size={18} />
              </Button>
            </div>
            
            <form onSubmit={handleSubmitShipping} className="p-4 space-y-4">
              <div className="bg-mist/50 p-3 rounded-md mb-4">
                <p className="text-sm font-medium">{selectedFulfillment.product_title}</p>
                <p className="text-xs text-muted-foreground">Custom T-Shirt • Est. delivery: 5-7 business days</p>
              </div>
              
              <div>
                <Label htmlFor="size">T-Shirt Size *</Label>
                <select
                  id="size"
                  value={shippingForm.size}
                  onChange={e => setShippingForm({...shippingForm, size: e.target.value})}
                  className="w-full mt-1 p-2 border rounded-md"
                  data-testid="size-select"
                >
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="2XL">2XL</option>
                  <option value="3XL">3XL</option>
                </select>
              </div>

              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={shippingForm.name}
                  onChange={e => setShippingForm({...shippingForm, name: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="shipping-name"
                />
              </div>

              <div>
                <Label htmlFor="address_line1">Address Line 1 *</Label>
                <Input
                  id="address_line1"
                  value={shippingForm.address_line1}
                  onChange={e => setShippingForm({...shippingForm, address_line1: e.target.value})}
                  placeholder="123 Main St"
                  required
                  data-testid="shipping-address1"
                />
              </div>

              <div>
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  value={shippingForm.address_line2}
                  onChange={e => setShippingForm({...shippingForm, address_line2: e.target.value})}
                  placeholder="Apt 4B (optional)"
                  data-testid="shipping-address2"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={shippingForm.city}
                    onChange={e => setShippingForm({...shippingForm, city: e.target.value})}
                    placeholder="New York"
                    required
                    data-testid="shipping-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State/Province *</Label>
                  <Input
                    id="state"
                    value={shippingForm.state}
                    onChange={e => setShippingForm({...shippingForm, state: e.target.value})}
                    placeholder="NY"
                    required
                    data-testid="shipping-state"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="postal_code">Postal Code *</Label>
                  <Input
                    id="postal_code"
                    value={shippingForm.postal_code}
                    onChange={e => setShippingForm({...shippingForm, postal_code: e.target.value})}
                    placeholder="10001"
                    required
                    data-testid="shipping-postal"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <select
                    id="country"
                    value={shippingForm.country}
                    onChange={e => setShippingForm({...shippingForm, country: e.target.value})}
                    className="w-full mt-1 p-2 border rounded-md h-10"
                    data-testid="shipping-country"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="AU">Australia</option>
                    <option value="NL">Netherlands</option>
                    <option value="IE">Ireland</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  value={shippingForm.phone}
                  onChange={e => setShippingForm({...shippingForm, phone: e.target.value})}
                  placeholder="+1 555 123 4567"
                  data-testid="shipping-phone"
                />
              </div>

              <div className="pt-4 flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowShippingModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                  data-testid="submit-shipping-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Clock size={14} className="animate-spin" /> Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Truck size={14} /> Complete Order
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
